import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Trash, PencilSimple, FloppyDisk, Archive, Star } from '@phosphor-icons/react';
import { API } from './adminConstants';
import { CsvExportButton } from './CsvHelpers';

export function JobManagement() {
  const { getAuthHeader } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [form, setForm] = useState({ title: '', ref: '', salary: '', contract: 'Permanent', location: 'North Somerset', hours: 'Full-time', department: '', description: '', responsibilities: '', requirements: '', closing_date: '', featured: false });

  const load = async () => { const r = await axios.get(`${API}/admin/jobs`, { headers: getAuthHeader() }); setJobs(r.data); };
  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const data = { ...form, responsibilities: form.responsibilities.split('\n').filter(Boolean), requirements: form.requirements.split('\n').filter(Boolean) };
    try {
      if (editJob) {
        await axios.put(`${API}/admin/jobs/${editJob.id}`, data, { headers: getAuthHeader() });
      } else {
        await axios.post(`${API}/admin/jobs`, data, { headers: getAuthHeader() });
      }
      toast.success(editJob ? 'Job updated' : 'Job created');
      setShowForm(false); setEditJob(null);
      setForm({ title: '', ref: '', salary: '', contract: 'Permanent', location: 'North Somerset', hours: 'Full-time', department: '', description: '', responsibilities: '', requirements: '', closing_date: '', featured: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (j) => {
    setEditJob(j);
    setForm({ ...j, responsibilities: (j.responsibilities || []).join('\n'), requirements: (j.requirements || []).join('\n'), featured: !!j.featured });
    setShowForm(true);
  };

  const toggleFeatured = async (j) => {
    try {
      await axios.put(`${API}/admin/jobs/${j.id}`, { featured: !j.featured }, { headers: getAuthHeader() });
      toast.success(j.featured ? 'Removed from featured' : 'Marked as featured');
      load();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/admin/jobs/${id}`, { headers: getAuthHeader() }); toast.success('Deleted'); load(); } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="job-management">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Job Listings</h1><p className="text-slate-500 text-sm">{jobs.length} total listings</p></div>
        <div className="flex gap-2">
          <CsvExportButton endpoint="/csv/export/jobs" label="Export" />
          <Button onClick={() => { setEditJob(null); setShowForm(true); }} className="bg-lime-600 hover:bg-lime-700" data-testid="create-job-btn"><Plus size={15} className="mr-1" />Add Job</Button>
        </div>
      </div>

      {showForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="job-form">
          <h3 className="font-semibold text-slate-900 mb-4">{editJob ? 'Edit Job' : 'New Job Listing'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="job-title-input" /></div>
            <div><Label className="text-xs">Reference</Label><Input value={form.ref} onChange={e => setForm({...form, ref: e.target.value})} /></div>
            <div><Label className="text-xs">Salary</Label><Input value={form.salary} onChange={e => setForm({...form, salary: e.target.value})} /></div>
            <div><Label className="text-xs">Department</Label><Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
            <div><Label className="text-xs">Contract</Label><Input value={form.contract} onChange={e => setForm({...form, contract: e.target.value})} /></div>
            <div><Label className="text-xs">Location</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} /></div>
            <div><Label className="text-xs">Hours</Label><Input value={form.hours} onChange={e => setForm({...form, hours: e.target.value})} /></div>
            <div><Label className="text-xs">Closing Date</Label><Input type="date" value={form.closing_date?.split('T')[0] || ''} onChange={e => setForm({...form, closing_date: e.target.value})} /></div>
          </div>
          <div className="mt-4 space-y-3">
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} /></div>
            <div><Label className="text-xs">Responsibilities (one per line)</Label><Textarea value={form.responsibilities} onChange={e => setForm({...form, responsibilities: e.target.value})} rows={3} /></div>
            <div><Label className="text-xs">Requirements (one per line)</Label><Textarea value={form.requirements} onChange={e => setForm({...form, requirements: e.target.value})} rows={3} /></div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100">
            <label className="flex items-start gap-3 cursor-pointer group" data-testid="job-featured-toggle-label">
              <div className="relative shrink-0 mt-0.5">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={e => setForm({ ...form, featured: e.target.checked })}
                  className="sr-only peer"
                  data-testid="job-featured-toggle"
                />
                <div className="w-10 h-5 bg-slate-200 peer-checked:bg-lime-600 rounded-full transition-colors duration-200" />
                <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5" />
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800">
                  <Star size={14} weight={form.featured ? 'fill' : 'regular'} className={form.featured ? 'text-lime-600' : 'text-slate-400'} />
                  Featured listing
                </div>
                <p className="text-[11px] text-slate-500">Pinned to the top of the public Careers page, regardless of date.</p>
              </div>
            </label>
          </div>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" onClick={() => { setShowForm(false); setEditJob(null); }}>Cancel</Button>
            <Button onClick={handleSave} className="bg-lime-600 hover:bg-lime-700" data-testid="save-job-btn"><FloppyDisk size={15} className="mr-1" />Save</Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="divide-y divide-slate-100">
          {jobs.map(j => (
            <div key={j.id} className="p-4 flex items-center justify-between" data-testid={`admin-job-${j.id}`}>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-900">{j.title}</p>
                  <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{j.ref}</span>
                  {j.featured && <span className="text-xs px-1.5 py-0.5 bg-lime-600 text-white rounded inline-flex items-center gap-1"><Star size={10} weight="fill" />Featured</span>}
                  {j.applications_count > 0 && <span className="text-xs px-1.5 py-0.5 bg-lime-100 text-lime-700 rounded">{j.applications_count} applications</span>}
                  {j.is_expired && <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Expired</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{j.department} | {j.location} | {j.salary}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => toggleFeatured(j)} className={j.featured ? 'border-lime-300 text-lime-700 hover:bg-lime-50' : ''} data-testid={`feature-job-${j.id}`}>
                  <Star size={14} weight={j.featured ? 'fill' : 'regular'} className={`mr-1 ${j.featured ? 'text-lime-600' : ''}`} />{j.featured ? 'Featured' : 'Feature'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => startEdit(j)}><PencilSimple size={14} className="mr-1" />Edit</Button>
                <Button variant="outline" size="sm" onClick={async () => { try { await axios.post(`${API}/admin/jobs/${j.id}/archive`, {}, { headers: getAuthHeader() }); toast.success('Job archived'); load(); } catch { toast.error('Failed to archive'); } }} className="text-amber-600 border-amber-200 hover:bg-amber-50" data-testid={`archive-job-${j.id}`}><Archive size={14} className="mr-1" />Archive</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(j.id)} className="text-red-500 border-red-200 hover:bg-red-50"><Trash size={14} /></Button>
              </div>
            </div>
          ))}
          {jobs.length === 0 && <div className="p-8 text-center text-slate-500">No job listings</div>}
        </div>
      </div>
    </div>
  );
}
